import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Skeleton } from '../components/ui/skeleton';
import { getProject, createProject, updateProject, errorMessage } from '../services/api';
import { toast } from 'sonner';
import { useFieldErrors } from '../lib/useFieldErrors';
import { FieldError } from '../components/FieldError';
import { ArrowLeft, Loader2, Upload, X, Film, Search } from 'lucide-react';
import axios from 'axios';

export default function ProjectForm() {
    const { id } = useParams();
    const navigate = useNavigate();
    const fileInputRef = useRef(null);
    const isEdit = !!id;
    
    const [loading, setLoading] = useState(isEdit);
    const [saving, setSaving] = useState(false);
    const [tmdbResults, setTmdbResults] = useState([]);
    const [tmdbSearching, setTmdbSearching] = useState(false);
    const [showTmdb, setShowTmdb] = useState(false);
    const { errors, fail, clearError, resetErrors, fieldProps } = useFieldErrors();
    const [formData, setFormData] = useState({
        title: '',
        original_title: '',
        production_year: '',
        poster_base64: '',
        archived: false,
        favorite: false,
        archive_destination: ''
    });

    useEffect(() => {
        if (isEdit) {
            fetchProject();
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- reload only when these inputs change; the loader is recreated each render and reads current state
    }, [id]);

    const fetchProject = async () => {
        try {
            const response = await getProject(id);
            const project = response.data;
            setFormData({
                title: project.title || '',
                original_title: project.original_title || '',
                production_year: project.production_year || '',
                poster_base64: project.poster_base64 || '',
                archived: project.archived || false,
                favorite: project.favorite || false,
                archive_destination: project.archive_destination || ''
            });
        } catch (error) {
            if (!error._isAuthError) toast.error(errorMessage(error, 'Erreur lors du chargement du projet'));
            navigate('/projects');
        } finally {
            setLoading(false);
        }
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        clearError(name);
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleSwitchChange = (name, checked) => {
        setFormData(prev => ({ ...prev, [name]: checked }));
    };

    const handleFileChange = (e) => {
        const file = e.target.files[0];
        if (!file) return;

        if (!file.type.startsWith('image/')) {
            toast.error('Veuillez sélectionner une image');
            return;
        }

        if (file.size > 5 * 1024 * 1024) {
            toast.error('L\'image ne doit pas dépasser 5 Mo');
            return;
        }

        const reader = new FileReader();
        reader.onload = (e) => {
            setFormData(prev => ({ ...prev, poster_base64: e.target.result }));
        };
        reader.readAsDataURL(file);
    };

    const handleRemovePoster = () => {
        setFormData(prev => ({ ...prev, poster_base64: '' }));
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    const handleTmdbSearch = async () => {
        if (!formData.title.trim()) {
            toast.error('Saisissez un titre pour rechercher');
            return;
        }
        setTmdbSearching(true);
        setShowTmdb(true);
        try {
            const params = new URLSearchParams({ query: formData.title });
            if (formData.production_year) params.set('year', formData.production_year);
            const resp = await axios.get(
                `${process.env.REACT_APP_BACKEND_URL}/api/tmdb/search?${params.toString()}`
            );
            setTmdbResults(resp.data);
            if (resp.data.length === 0) toast.info('Aucun résultat trouvé');
        } catch (error) {
            toast.error(errorMessage(error, 'Erreur lors de la recherche TMDB'));
        } finally {
            setTmdbSearching(false);
        }
    };

    const handleSelectTmdbPoster = async (result) => {
        try {
            const resp = await fetch(result.poster_lg);
            const blob = await resp.blob();
            const reader = new FileReader();
            reader.onload = (e) => {
                setFormData(prev => ({
                    ...prev,
                    poster_base64: e.target.result,
                    original_title: prev.original_title || result.original_title || '',
                    production_year: prev.production_year || result.year || ''
                }));
                setShowTmdb(false);
                toast.success('Affiche sélectionnée');
            };
            reader.readAsDataURL(blob);
        } catch (error) {
            toast.error(errorMessage(error, 'Erreur lors du téléchargement de l\'affiche'));
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (!formData.title.trim()) {
            fail('title', 'Le titre est requis');
            return;
        }

        setSaving(true);

        try {
            const data = {
                ...formData,
                production_year: formData.production_year ? parseInt(formData.production_year) : null
            };

            if (isEdit) {
                await updateProject(id, data);
                toast.success('Projet mis à jour');
            } else {
                const response = await createProject(data);
                toast.success('Projet créé');
                navigate(`/projects/${response.data.id}`);
                return;
            }
            navigate(`/projects/${id}`);
        } catch (error) {
            toast.error(errorMessage(error, 'Erreur lors de l\'enregistrement'));
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return (
            <Layout>
                <div className="p-4 sm:p-6 max-w-2xl mx-auto space-y-6">
                    <Skeleton className="h-8 w-48" />
                    <Skeleton className="h-96" />
                </div>
            </Layout>
        );
    }

    return (
        <Layout>
            <div className="p-4 sm:p-6 max-w-2xl mx-auto space-y-6 animate-fade-in" data-testid="project-form">
                {/* Header */}
                <div className="flex items-center gap-4">
                    <Button 
                        variant="ghost" 
                        size="icon" aria-label="Retour" 
                        onClick={() => navigate(isEdit ? `/projects/${id}` : '/projects')}
                        data-testid="back-button"
                    >
                        <ArrowLeft className="w-5 h-5" />
                    </Button>
                    <h1 className="font-heading text-3xl font-bold tracking-tight">
                        {isEdit ? 'MODIFIER LE PROJET' : 'NOUVEAU PROJET'}
                    </h1>
                </div>

                {/* Form */}
                <Card className="bg-card border-border">
                    <CardContent className="pt-6">
                        <form noValidate onSubmit={handleSubmit} className="space-y-6">
                            {/* Poster */}
                            <div className="space-y-2">
                                <Label>Affiche du film</Label>
                                <div className="flex gap-4 items-start">
                                    <div className="w-32 h-48 bg-secondary rounded overflow-hidden flex-shrink-0">
                                        {formData.poster_base64 ? (
                                            <img
                                                src={formData.poster_base64}
                                                alt="Affiche"
                                                className="w-full h-full object-cover"
                                            />
                                        ) : (
                                            <div className="w-full h-full flex items-center justify-center">
                                                <Film className="w-10 h-10 text-muted-foreground/30" />
                                            </div>
                                        )}
                                    </div>
                                    <div className="flex flex-col gap-2">
                                        <input
                                            ref={fileInputRef}
                                            type="file"
                                            accept="image/*"
                                            onChange={handleFileChange}
                                            className="hidden"
                                            data-testid="poster-input"
                                        />
                                        <Button 
                                            type="button" 
                                            variant="outline"
                                            onClick={() => fileInputRef.current?.click()}
                                        >
                                            <Upload className="w-4 h-4 mr-2" />
                                            Choisir une image
                                        </Button>
                                        <Button
                                            type="button"
                                            variant="outline"
                                            onClick={handleTmdbSearch}
                                            disabled={tmdbSearching}
                                            data-testid="tmdb-search-button"
                                        >
                                            {tmdbSearching ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Search className="w-4 h-4 mr-2" />}
                                            Rechercher sur TMDB
                                        </Button>
                                        {formData.poster_base64 && (
                                            <Button 
                                                type="button" 
                                                variant="ghost"
                                                size="sm"
                                                onClick={handleRemovePoster}
                                            >
                                                <X className="w-4 h-4 mr-2" />
                                                Supprimer
                                            </Button>
                                        )}
                                        <p className="text-xs text-muted-foreground">
                                            JPG, PNG ou WebP, max 5 Mo
                                        </p>
                                    </div>
                                </div>

                                {/* TMDB Results */}
                                {showTmdb && (
                                    <div className="mt-3 p-3 bg-secondary/50 rounded-sm border border-border" data-testid="tmdb-results">
                                        <div className="flex items-center justify-between mb-2">
                                            <span className="text-sm font-medium">Résultats TMDB</span>
                                            <Button type="button" variant="ghost" size="sm" aria-label="Fermer les résultats TMDB" onClick={() => setShowTmdb(false)}>
                                                <X className="w-3 h-3" />
                                            </Button>
                                        </div>
                                        {tmdbSearching ? (
                                            <div className="flex justify-center py-4">
                                                <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
                                            </div>
                                        ) : tmdbResults.length === 0 ? (
                                            <p className="text-sm text-muted-foreground text-center py-4">Aucun résultat</p>
                                        ) : (
                                            <div className="grid grid-cols-4 gap-2">
                                                {tmdbResults.map((r) => (
                                                    <button
                                                        key={r.id}
                                                        type="button"
                                                        onClick={() => handleSelectTmdbPoster(r)}
                                                        className="group rounded overflow-hidden border border-transparent hover:border-primary transition-colors"
                                                        data-testid={`tmdb-result-${r.id}`}
                                                    >
                                                        <img
                                                            src={r.poster_sm}
                                                            alt={r.title}
                                                            className="w-full aspect-[2/3] object-cover group-hover:opacity-80 transition-opacity"
                                                        />
                                                        <div className="p-1 text-center">
                                                            <p className="text-xs font-medium truncate">{r.title}</p>
                                                            <p className="text-xs text-muted-foreground">{r.year}</p>
                                                        </div>
                                                    </button>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>

                            {/* Title */}
                            <div className="space-y-2">
                                <Label htmlFor="title">Titre *</Label>
                                <Input
                                    id="title"
                                    {...fieldProps('title')}
                                    name="title"
                                    value={formData.title}
                                    onChange={handleChange}
                                    placeholder="Ex: Terminator"
                                    required
                                    data-testid="title-input"
                                />
                                <FieldError id="title" message={errors['title']} />
                            </div>

                            {/* Original Title */}
                            <div className="space-y-2">
                                <Label htmlFor="original_title">Titre original</Label>
                                <Input
                                    id="original_title"
                                    name="original_title"
                                    value={formData.original_title}
                                    onChange={handleChange}
                                    placeholder="Si différent du titre"
                                    data-testid="original-title-input"
                                />
                            </div>

                            {/* Production Year */}
                            <div className="space-y-2">
                                <Label htmlFor="production_year">Année de production</Label>
                                <Input
                                    id="production_year"
                                    name="production_year"
                                    type="number"
                                    min="1900"
                                    max="2100"
                                    value={formData.production_year}
                                    onChange={handleChange}
                                    placeholder="Ex: 1984"
                                    data-testid="year-input"
                                />
                            </div>

                            {/* Switches */}
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                <div className="flex items-center justify-between p-4 rounded-sm bg-secondary/30">
                                    <Label htmlFor="favorite" className="cursor-pointer">Favori</Label>
                                    <Switch
                                        id="favorite"
                                        checked={formData.favorite}
                                        onCheckedChange={(checked) => handleSwitchChange('favorite', checked)}
                                        data-testid="favorite-switch"
                                    />
                                </div>
                                <div className="flex items-center justify-between p-4 rounded-sm bg-secondary/30">
                                    <Label htmlFor="archived" className="cursor-pointer">Archivé</Label>
                                    <Switch
                                        id="archived"
                                        checked={formData.archived}
                                        onCheckedChange={(checked) => handleSwitchChange('archived', checked)}
                                        data-testid="archived-switch"
                                    />
                                </div>
                                {formData.archived && (
                                    <div className="space-y-2">
                                        <Label htmlFor="archive_destination">Destination archive</Label>
                                        <Input
                                            id="archive_destination"
                                            value={formData.archive_destination}
                                            onChange={(e) => setFormData(prev => ({ ...prev, archive_destination: e.target.value }))}
                                            placeholder="Ex: Stockage externe, Cave, Entrepôt..."
                                            data-testid="archive-destination-input"
                                        />
                                    </div>
                                )}
                            </div>

                            {/* Actions */}
                            <div className="flex gap-4 pt-4">
                                <Button
                                    type="button"
                                    variant="outline"
                                    onClick={() => navigate(isEdit ? `/projects/${id}` : '/projects')}
                                    className="flex-1"
                                >
                                    Annuler
                                </Button>
                                <Button
                                    type="submit"
                                    disabled={saving}
                                    className="flex-1"
                                    data-testid="submit-button"
                                >
                                    {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                                    {isEdit ? 'Enregistrer' : 'Créer le projet'}
                                </Button>
                            </div>
                        </form>
                    </CardContent>
                </Card>
            </div>
        </Layout>
    );
}
